"use client";

import { onboardingProps, useOnboardingStore } from "@/store/onboardingStore";
import { cn } from "@/utils/utils";
import { usePathname } from "next/navigation";
import { ReactNode, useEffect, useRef } from "react";
import {
  DotPatternCanvas,
  DotPatternCanvasRef,
} from "./components/dot-pattern-canvas";
import { LeftContent } from "./components/left-content";
import { LoadingCard } from "./components/loading-card";
import DefaultRight from "./@right/default";
import {
  ONBOARDING_RIGHT_PANEL_MODE,
  getOnboardingStepUi,
} from "@jurni/schemas";
import { getStepNameFromPathname } from "@/utils/onboarding";

interface OnboardingLayoutProps {
  left: ReactNode;
  right: ReactNode;
}

export default function OnboardingLayout({
  left,
  right,
}: OnboardingLayoutProps) {
  const pathname = usePathname();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dotPatternCanvasRef = useRef<DotPatternCanvasRef>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onboardingLoadingState = useOnboardingStore(
    onboardingProps.onboardingLoadingState,
  );
  const currentRouteStepName = getStepNameFromPathname(pathname);
  const currentRouteStepUi = getOnboardingStepUi(currentRouteStepName);
  const loadingStepUi = getOnboardingStepUi(onboardingLoadingState ?? undefined);
  const activePanelUi = onboardingLoadingState
    ? loadingStepUi
    : currentRouteStepUi;
  const showFocusedRightPanel =
    loadingStepUi.focusRightPanel || currentRouteStepUi.focusRightPanel;
  const usesRouteRightPanel =
    currentRouteStepUi.rightPanel === ONBOARDING_RIGHT_PANEL_MODE.ROUTE;

  useEffect(() => {
    // Scroll to top when pathname changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [pathname]);

  useEffect(() => {
    if (onboardingLoadingState) {
      dotPatternCanvasRef.current?.triggerRipple();
      intervalRef.current = setInterval(() => {
        dotPatternCanvasRef.current?.triggerRipple();
      }, 2000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [onboardingLoadingState]);

  return (
    <div
      ref={scrollContainerRef}
      className={
        "bg-onboarding-background relative flex h-screen w-full overflow-y-auto"
      }
    >
      <div
        className={cn(
          "relative h-fit w-full overflow-hidden opacity-100 transition-all duration-500 ease-in-out lg:w-[46%]",
          activePanelUi.collapseLeftWhenFocused && "!w-0 opacity-0",
          activePanelUi.collapseLeftOnMobileWhenFocused &&
            "max-lg:!w-0 max-lg:opacity-0",
        )}
      >
        <div className="relative h-fit w-full overflow-hidden px-3 py-3 lg:pr-0">
          <LeftContent content={left} />
        </div>
      </div>
      <div
        className={cn(
          "sticky top-0 h-screen w-0 min-w-0 overflow-hidden transition-all duration-500 ease-in-out lg:w-[54%]",
          showFocusedRightPanel && "!w-full",
        )}
      >
        <div
          className={cn(
            "hidden h-full w-full lg:flex",
            showFocusedRightPanel && "!flex",
          )}
        >
          <DotPatternCanvas ref={dotPatternCanvasRef} glow={true} />
          <img
            alt="gradient-ellipse"
            className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 opacity-60"
            src="/images/onboarding/ellipse.svg"
          />
          <div className="relative flex h-full w-full flex-col">
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
                onboardingLoadingState
                  ? "opacity-100"
                  : "pointer-events-none opacity-0"
              }`}
            >
              <LoadingCard />
            </div>
            <div
              className={`relative flex h-full w-full flex-col transition-opacity duration-500 ${
                !onboardingLoadingState
                  ? "opacity-100"
                  : "pointer-events-none opacity-0"
              }`}
            >
              {usesRouteRightPanel ? right : <DefaultRight />}
            </div>
          </div>
          <div
            className="pointer-events-none absolute inset-0 z-30"
            style={{
              background:
                "radial-gradient(circle at center, transparent 0%, transparent 45%, rgba(37, 37, 39, 0.8) 75%, #252527 98%)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
